import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchApplicationDetails, uploadFormFile } from '../../../utils/apiUtils';
import styled from 'styled-components';
import { saveFormData } from '../../../utils/apiUtils';
import FilePreviewModal from '../../common/FilePreviewModal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FormPageNavigator from '../../common/FormPageNavigator';
import SessionTimer from '../../common/SessionTimer';

// Helper function to generate a unique application ID
const generateApplicationId = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const year = String(now.getFullYear());
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  const randomChar = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)];
  return `CNG-${day}${month}${year}${hours}${minutes}${seconds}-${milliseconds}${randomChar}`;
};

// A4 Dimensions (approximate for screen, maintaining aspect ratio)
// 210mm / 297mm = 0.707
const A4_WIDTH_PX = 794; // Approx 210mm at 96 DPI
const A4_HEIGHT_PX = 1123; // Approx 297mm at 96 DPI

// Progress bar at top of page
const ProgressBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  transition: width 0.5s ease-in-out;
`;

const ProgressLabel = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: #f8fcfa;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 1000;
`;

// Zoom controls
const ZoomControlsContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
`;

const ZoomButton = styled.button`
  width: 30px;
  height: 30px;
  background-color: white;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  padding: 0;
  
  &:hover {
    background-color: #f8fcfa;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const OfflineIndicator = styled.div`
  background-color: #dc3545; /* Red for offline */
  color: white;
  text-align: center;
  padding: 10px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2000; /* Ensure it's above other elements */
  font-size: 0.9em;
`;

const PageWrapper = styled.div<{ scale: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 100vh;
  background-color: #f5f5f5;
  transform: scale(${props => props.scale});
  transform-origin: center top;
  transition: transform 0.3s ease;
`;

const A4PageContainer = styled.div`
  width: ${A4_WIDTH_PX}px;
  min-height: ${A4_HEIGHT_PX}px; /* Use min-height to allow content to expand if needed */
  background-color: white;
  padding: 50px; /* Increased padding for more breathing room */
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07); /* Softer, more modern shadow */
  box-sizing: border-box;
  position: relative; /* For page number positioning */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  color: #212529; /* Darker, more standard text color */
  font-size: 13px; /* Base font size for content */
  line-height: 1.6;
`;

const FloatingBackButton = styled.button`
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(145deg, #4a6cf7, #5e54e2); /* Modern gradient */
  color: white;
  border: none;
  border-radius: 50%; /* Circular button */
  width: 60px; /* Size of the button */
  height: 60px;
  font-size: 22px; /* Slightly smaller icon size for a cleaner look */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(74, 108, 247, 0.3), 0 3px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000; /* Ensure it's on top */
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 10px 20px rgba(74, 108, 247, 0.4), 0 6px 6px rgba(0, 0, 0, 0.1);
    
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 3px 8px rgba(74, 108, 247, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &::after {
    content: 'Back';
    position: absolute;
    font-size: 12px;
    bottom: 7px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const FormContentContainer = styled.div`
  /* Form content styling */
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  font-size: 0.8em;
  color: #777;
`;

const NextPageButton = styled.button`
  position: absolute;
  bottom: 15px;
  right: 60px;
  background-color: #6cb288;
  color: white;
  padding: 8px 25px;
  border: none;
  border-radius: 4px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #4d9e70;
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const SaveIndicator = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 50%;
  left: 20px;
  transform: translateY(-50%);
  background-color: #f8fcfa;
  color: #6cb288;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #a8d0b9;
  font-size: 0.8em;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 10000;
`;

const SaveDraftButton = styled.button`
  background-color: #ffc107; /* Amber color for draft */
  color: #212529; /* Dark text for contrast */
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 15px; /* Space from submit button or other elements */
  margin-right: 10px; /* Space from submit button if on same line */
  display: inline-block; /* Allow side-by-side with submit if desired */
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #e0a800; /* Darker amber on hover */
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #c69500;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const PayNowButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 6px; /* Slightly larger radius */
  font-size: 1em; /* Relative to base */
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 30px; /* More space above */
  display: block; 
  margin-left: auto;
  margin-right: auto;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #0069d9; /* Darker shade on hover */
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #0056b3;
    transform: translateY(1px); /* Slight press effect */
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
`;

const ActionButtonsContainer = styled.div`
  margin-top: 30px;
  width: 100%;
`;

// Form-specific styles
const FormSection = styled.div`
  margin-bottom: 25px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  min-height: 80px;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #6cb288;
    box-shadow: 0 0 0 2px rgba(108, 178, 136, 0.2);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  font-size: 12px;
`;

const TableHeader = styled.th`
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
`;

const TableCell = styled.td`
  border: 1px solid #ddd;
  padding: 6px;
`;

const TableInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  padding: 4px;
  font-size: 12px;
  
  &:focus {
    outline: 1px solid #6cb288;
    background-color: #f8fcfa;
  }
`;

const AddRowButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-bottom: 10px;
  
  &:hover {
    background-color: #218838;
  }
`;

const RemoveRowButton = styled.button`
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  cursor: pointer;
  
  &:hover {
    background-color: #c82333;
  }
`;

const FileInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  
  &:focus {
    outline: none;
    border-color: #6cb288;
  }
`;

const FileUploadSection = styled.div`
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  border: 1px solid #a8d0b9; /* Greenish border */
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 1em;
  font-family: inherit;
  background-color: #f8fcfa; /* Very subtle green tint */
  color: #495057;
  line-height: 1.5;
  resize: vertical; /* Allow vertical resize, disable horizontal */
  min-height: 80px; /* Approx 4 lines of text, increased from 40px */
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #6cb288; /* Stronger green when focused */
    box-shadow: 0 0 0 0.2rem rgba(108, 178, 136, 0.25);
  }

  &:hover {
    border-color: #8ac0a0;
  }

  &::placeholder {
    color: #7ea38c;
    opacity: 1; 
  }
`;

const FileUploadLabel = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  font-size: 13px;
  color: #2c3e50;
`;

const PreviewButton = styled.button`
  background-color: #17a2b8;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 11px;
  cursor: pointer;
  margin-left: 10px;
  
  &:hover {
    background-color: #138496;
  }
  
  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

// Account details row interface
interface AccountRow {
  id: string;
  type: string;
  details: string;
  holderName: string;
  relationship: string;
  amount: string;
}

interface FormData {
  applicationNoOfficeUse: string;
  accountDetails: AccountRow[];
  anyOtherDetails: string;
  
  // File uploads
  ownershipDocument: File | string | null;
  searchTitleReport: File | string | null;
  notarizedAffidavit: File | string | null;
  propertyCard: File | string | null;
  ferfarMutation: File | string | null;
  demarcationPlan: File | string | null;
  plotDetails: File | string | null;
  roadDPPlan: File | string | null;
  dpOpinion: File | string | null;
  zoneCertificate: File | string | null;
  propertyTaxReceipts: File | string | null;
  partnershipDeed: File | string | null;
  registrationCertificate: File | string | null;
  landLeaseAgreement: File | string | null;
  faLnMap: File | string | null;
  landUsageClearance: File | string | null;
  surveyPlan: File | string | null;
  nocLandlord: File | string | null;
  nocAuthority: File | string | null;
  advocateOpinion: File | string | null;
  consentLetters: File | string | null;
  
  errors?: {
    accountDetails?: string;
    anyOtherDetails?: string;
    [key: string]: string | undefined;
  };
}

interface ApplicationFormPage5Props {
  formData?: any;
  isViewMode?: boolean;
}

const initialState: FormData = {
  applicationNoOfficeUse: '',
  accountDetails: [{ id: '1', type: '', details: '', holderName: '', relationship: '', amount: '' }],
  anyOtherDetails: '',
  ownershipDocument: null,
  searchTitleReport: null,
  notarizedAffidavit: null,
  propertyCard: null,
  ferfarMutation: null,
  demarcationPlan: null,
  plotDetails: null,
  roadDPPlan: null,
  dpOpinion: null,
  zoneCertificate: null,
  propertyTaxReceipts: null,
  partnershipDeed: null,
  registrationCertificate: null,
  landLeaseAgreement: null,
  faLnMap: null,
  landUsageClearance: null,
  surveyPlan: null,
  nocLandlord: null,
  nocAuthority: null,
  advocateOpinion: null,
  consentLetters: null,
  errors: {}
};

const ApplicationFormPage5: React.FC<ApplicationFormPage5Props> = ({ isViewMode = false, formData: propFormData }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [applicationId, setApplicationId] = useState<string>('');
  const isNewlyGeneratedIdRef = useRef<boolean>(false);
  
  // Progress bar state
  const [formProgress, setFormProgress] = useState<number>(0);
  
  // Zoom state
  const [zoomScale, setZoomScale] = useState<number>(1.0);
  
  // Check if Fill Dummy Data button should be shown (controlled from Admin Controls, default OFF)
  const [showDummyDataButton, setShowDummyDataButton] = useState<boolean>(false);
  
  // Fetch showDummyDataButton setting from backend
  useEffect(() => {
    const fetchDummyDataSetting = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const API_BASE = API_URL === '/api' ? '' : API_URL;
        const response = await fetch(`${API_BASE}/api/public/settings`);
        if (response.ok) {
          const data = await response.json();
          const settings = data.settings || data;
          if (settings && typeof settings.showDummyDataButton === 'boolean') {
            setShowDummyDataButton(settings.showDummyDataButton);
          }
        }
      } catch (error) {
        console.error('Error fetching dummy data button setting:', error);
      }
    };
    fetchDummyDataSetting();
  }, []);
  
  // Form data state
  const [formData, setFormData] = useState<FormData>(() => {
    const source = propFormData || initialState;
    return {
      ...initialState,
      ...source,
      accountDetails: (source.accountDetails && source.accountDetails.length > 0) ? source.accountDetails : initialState.accountDetails,
    };
  });

  // State for save indicator
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string>('Saved');
  const [showSaveIndicator, setShowSaveIndicator] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [showBrochureModal, setShowBrochureModal] = useState<boolean>(false);

  // File upload states
  type UploadableField = keyof Pick<FormData, 
    'ownershipDocument' | 'searchTitleReport' | 'notarizedAffidavit' | 'propertyCard' | 
    'ferfarMutation' | 'demarcationPlan' | 'plotDetails' | 'roadDPPlan' | 
    'dpOpinion' | 'zoneCertificate' | 'propertyTaxReceipts' | 'partnershipDeed' | 
    'registrationCertificate' | 'landLeaseAgreement' | 'faLnMap' | 'landUsageClearance' | 
    'surveyPlan' | 'nocLandlord' | 'nocAuthority' | 'advocateOpinion' | 'consentLetters'
  >;
  
  const [uploadStatus, setUploadStatus] = useState<{[key in UploadableField]?: 'idle' | 'uploading' | 'success' | 'error'}>({
    ownershipDocument: 'idle',
    searchTitleReport: 'idle',
    notarizedAffidavit: 'idle',
    propertyCard: 'idle',
    ferfarMutation: 'idle',
    demarcationPlan: 'idle',
    plotDetails: 'idle',
    roadDPPlan: 'idle',
    dpOpinion: 'idle',
    zoneCertificate: 'idle',
    propertyTaxReceipts: 'idle',
    partnershipDeed: 'idle',
    registrationCertificate: 'idle',
    landLeaseAgreement: 'idle',
    faLnMap: 'idle',
    landUsageClearance: 'idle',
    surveyPlan: 'idle',
    nocLandlord: 'idle',
    nocAuthority: 'idle',
    advocateOpinion: 'idle',
    consentLetters: 'idle'
  });
  const [uploadProgress, setUploadProgress] = useState<{[key in UploadableField]?: number}>({
    ownershipDocument: 0,
    searchTitleReport: 0,
    notarizedAffidavit: 0,
    propertyCard: 0,
    ferfarMutation: 0,
    demarcationPlan: 0,
    plotDetails: 0,
    roadDPPlan: 0,
    dpOpinion: 0,
    zoneCertificate: 0,
    propertyTaxReceipts: 0,
    partnershipDeed: 0,
    registrationCertificate: 0,
    landLeaseAgreement: 0,
    faLnMap: 0,
    landUsageClearance: 0,
    surveyPlan: 0,
    nocLandlord: 0,
    nocAuthority: 0,
    advocateOpinion: 0,
    consentLetters: 0
  });
  const [uploadMessage, setUploadMessage] = useState<{[key in UploadableField]?: string}>({
    ownershipDocument: '',
    searchTitleReport: '',
    notarizedAffidavit: '',
    propertyCard: '',
    ferfarMutation: '',
    demarcationPlan: '',
    plotDetails: '',
    roadDPPlan: '',
    dpOpinion: '',
    zoneCertificate: '',
    propertyTaxReceipts: '',
    partnershipDeed: '',
    registrationCertificate: '',
    landLeaseAgreement: '',
    faLnMap: '',
    landUsageClearance: '',
    surveyPlan: '',
    nocLandlord: '',
    nocAuthority: '',
    advocateOpinion: '',
    consentLetters: ''
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalFileUrl, setModalFileUrl] = useState<string | null>(null);
  const [modalFileName, setModalFileName] = useState<string | null>(null);
  const [modalFileType, setModalFileType] = useState<string | null>(null);
  
  // PDF generation state
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);

  // Save timeout reference for debouncing
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Effect to initialize or generate applicationId
  useEffect(() => {
    if (propFormData?.applicationNoOfficeUse) {
      setApplicationId(propFormData.applicationNoOfficeUse);
      return;
    }

    let currentApplicationId = location.state?.applicationId || sessionStorage.getItem('currentApplicationId');
    let source = '';

    if (currentApplicationId) {
      source = location.state?.applicationId ? 'location.state' : 'sessionStorage';
      console.log(`[ApplicationFormPage5 Effect InitID] Found existing Application ID: ${currentApplicationId} from ${source}`);
      setApplicationId(currentApplicationId);
    } else {
      const newId = generateApplicationId();
      console.log(`[ApplicationFormPage5 Effect InitID] No existing ID found. Generated new Application ID: ${newId}`);
      setApplicationId(newId);
      sessionStorage.setItem('currentApplicationId', newId);
      isNewlyGeneratedIdRef.current = true;
    }
  }, [location.state, propFormData]);

  // Effect to load existing application data when resuming a draft
  useEffect(() => {
    if (propFormData) return; // Skip loading if formData is passed as a prop

    const loadDraftData = async () => {
      if (applicationId && !isNewlyGeneratedIdRef.current && !isViewMode) {
        console.log(`[ApplicationFormPage5 LoadDraft] Attempting to load draft data for ID: ${applicationId}`);
        try {
          const appData = await fetchApplicationDetails(applicationId);
          if (appData && appData.formData) {
            const loadedFormData = {
              ...initialState,
              ...appData.formData,
              accountDetails: (appData.formData.accountDetails && appData.formData.accountDetails.length > 0)
                ? appData.formData.accountDetails
                : initialState.accountDetails,
            };
            setFormData(loadedFormData);
            console.log(`[ApplicationFormPage5 LoadDraft] Successfully loaded draft data for ID: ${applicationId}`, appData);
          } else {
            console.warn(`[ApplicationFormPage5 LoadDraft] No application data or formData found for ID: ${applicationId}`, appData);
          }
        } catch (error) {
          console.error(`[ApplicationFormPage5 LoadDraft] Error loading draft data for ${applicationId}:`, error);
        }
      }
    };

    loadDraftData();
  }, [applicationId, isViewMode, propFormData]);

  // Note: We use process.env.NODE_ENV directly in the handleOpenFilePreview function

  const handleOpenFilePreview = (file: File | string | null, defaultFileName: string = 'file') => {
    console.log('[handleOpenFilePreview] Called with file:', file, 'defaultFileName:', defaultFileName);
    if (!file) {
      console.log('[handleOpenFilePreview] File is null, returning.');
      return;
    }

    let url: string | null = null;
    let name: string | null = null;
    let type: string | null = null;

    if (typeof file === 'string') {
      console.log('[handleOpenFilePreview] File is a string path:', file);
      
      // Determine backend origin consistently using REACT_APP_API_URL if absolute; otherwise fallback to current origin
      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      let serverBaseForFiles = window.location.origin;
      if (rawApiBaseUrl.startsWith('http')) {
        try {
          const parsed = new URL(rawApiBaseUrl);
          serverBaseForFiles = `${parsed.protocol}//${parsed.host}`;
        } catch (e) {
          console.warn('[handleOpenFilePreview] Could not parse REACT_APP_API_URL, using window.origin instead:', e);
          serverBaseForFiles = window.location.origin;
        }
      } else {
        // Relative API URL - check if we're in development
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isDevelopment && window.location.port === '3000') {
          serverBaseForFiles = `${window.location.protocol}//${window.location.hostname}:5000`;
        }
      }

      // Normalize to a path (strip any accidental origin and ensure leading slash)
      let normalizedPath = file;
      if (file.includes('://')) {
        try { normalizedPath = new URL(file).pathname; } catch { normalizedPath = file; }
      } else if (!file.startsWith('/')) {
        normalizedPath = `/${file}`;
      }

      url = `${serverBaseForFiles}${normalizedPath}`;
      name = normalizedPath.split('/').pop() || defaultFileName;

      // Infer MIME type based on file extension
      const lower = name.toLowerCase();
      if (lower.endsWith('.pdf')) {
        type = 'application/pdf';
      } else if (lower.match(/\.(jpeg|jpg|gif|png)$/i)) {
        const ext = lower.split('.').pop();
        type = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      } else if (lower.match(/\.(doc|docx)$/i)) {
        type = 'application/msword';
      } else {
        type = 'application/octet-stream';
      }
      
      console.log(`[handleOpenFilePreview] Constructed URL for string path: ${url}, Name: ${name}, Type: ${type}`);
    } else {
      // It's a File object
      url = URL.createObjectURL(file);
      name = file.name;
      type = file.type;
      console.log('[handleOpenFilePreview] Created object URL for File object:', url, 'Name:', name, 'Type:', type);
    }

    setModalFileUrl(url);
    setModalFileName(name);
    setModalFileType(type);
    setIsModalOpen(true);
    console.log('[handleOpenFilePreview] Modal state set. isOpen:', true, 'Url:', url);
  };

  const handleDownloadPdf = async () => {
    setIsPdfGenerating(true);
    setCompressionProgress(0);
    try {
      if (formRef.current) {
        // Create a clone of the form for PDF generation to avoid modifying the original
        const cloneContainer = document.createElement('div');
        cloneContainer.style.position = 'absolute';
        cloneContainer.style.left = '-9999px';
        cloneContainer.style.top = '-9999px';
        cloneContainer.style.width = `${formRef.current.offsetWidth}px`;
        document.body.appendChild(cloneContainer);
        
        // Clone the form content
        const formClone = formRef.current.cloneNode(true) as HTMLElement;
        cloneContainer.appendChild(formClone);
        
        // 1. First apply general styles to the clone
        formClone.style.fontSize = '12px'; // Reduce default font size
        formClone.style.lineHeight = '1.3'; // Tighter line height
        
        // 2. Find and process tables to ensure data is visible
        const tables = formClone.querySelectorAll('table');
        tables.forEach(table => {
          // Find if this is the Account Details table by checking header text
          const headerRow = table.querySelector('tr');
          if (headerRow) {
            const headers = Array.from(headerRow.querySelectorAll('th')).map(th => th.textContent?.trim());
            if (
              headers.length >= 5 &&
              headers[0]?.toLowerCase().includes('type') &&
              headers[1]?.toLowerCase().includes('details') &&
              headers[2]?.toLowerCase().includes('name') &&
              headers[3]?.toLowerCase().includes('relationship') &&
              headers[4]?.toLowerCase().includes('amount')
            ) {
              // This is the Account Details table: replace with card layout for PDF
              // Remove the table from the PDF clone
              const parent = table.parentElement;
              if (parent) {
                // Gather all rows except header
                const dataRows = Array.from(table.querySelectorAll('tr')).slice(1);
                // Helper to extract value from cell
                const getCellValue = (cell: Element): string => {
                  const input = cell.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null;
                  if (input) return input.value;
                  return cell.textContent || '';
                };
                dataRows.forEach(row => {
                  const cells = Array.from(row.querySelectorAll('td'));
                  if (cells.length >= 5) {
                    const cardDiv = document.createElement('div');
                    cardDiv.style.border = '1px solid #ccc';
                    cardDiv.style.margin = '16px 0';
                    cardDiv.style.padding = '12px 18px';
                    cardDiv.style.borderRadius = '8px';
                    cardDiv.style.background = '#fff';
                    cardDiv.style.fontSize = '13px';
                    cardDiv.style.lineHeight = '1.6';
                    cardDiv.style.color = '#222';
                    cardDiv.style.boxShadow = '0 2px 6px rgba(0,0,0,0.03)';
                    
                    // Add each field as a block
                    const fields = [
                      { label: 'Type', value: getCellValue(cells[0]) },
                      { label: 'Details of A/c, etc.', value: getCellValue(cells[1]) },
                      { label: 'Name of the Holder', value: getCellValue(cells[2]) },
                      { label: 'Relationship', value: getCellValue(cells[3]) },
                      { label: 'Amount', value: getCellValue(cells[4]) }
                    ];
                    fields.forEach(f => {
                      const line = document.createElement('div');
                      line.innerHTML = `<b>${f.label}:</b> <span style=\"white-space:pre-wrap;word-break:break-all;\">${f.value ?? ''}</span>`;
                      line.style.marginBottom = '4px';
                      cardDiv.appendChild(line);
                    });
                    parent.insertBefore(cardDiv, table);
                  }
                });
                parent.removeChild(table);
              }
              return; // Don't apply further table logic to this table
            }
          }
          // ...continue with normal table logic for non-account tables
          (table as HTMLElement).style.width = '100%';
          (table as HTMLElement).style.tableLayout = 'auto'; // allow columns to size to content
          const allRows = table.querySelectorAll('tr');
          allRows.forEach(row => {
            const rowCells = row.querySelectorAll('td, th');
            rowCells.forEach(cell => {
              const cellEl = cell as HTMLElement;
              if (cellEl.style.display !== 'none') { // Skip hidden cells
                cellEl.style.fontSize = '13px';
                cellEl.style.padding = '15px';
                cellEl.style.height = '';
                cellEl.style.minHeight = '';
                cellEl.style.verticalAlign = 'middle';
                cellEl.style.overflow = 'visible';
                cellEl.style.wordBreak = 'break-all'; // force break anywhere
                cellEl.style.overflowWrap = 'break-word';
                cellEl.style.whiteSpace = 'pre-wrap';
                cellEl.style.lineHeight = '1.4';
              }
            });
          });
        });
        
        // 3. Fix textarea for 'Any Other Details' to show all content
        const textareas = formClone.querySelectorAll('textarea');
        textareas.forEach(textarea => {
          // Create a div to replace the textarea for better display
          const contentDiv = document.createElement('div');
          contentDiv.textContent = textarea.value;
          contentDiv.style.border = '1px solid #ccc';
          contentDiv.style.padding = '8px';
          contentDiv.style.minHeight = '100px';
          contentDiv.style.width = '100%';
          contentDiv.style.wordBreak = 'break-word';
          contentDiv.style.whiteSpace = 'pre-wrap';
          contentDiv.style.fontSize = '10px';
          contentDiv.style.lineHeight = '1.3';
          
          // Replace textarea with div for better content display
          textarea.parentNode?.replaceChild(contentDiv, textarea);
        });
        
        // 4. Fix file upload elements and preview buttons
        const fileInputs = formClone.querySelectorAll('input[type="file"]');
        fileInputs.forEach(input => {
          // Apply styles to file input parents to ensure visibility
          let parent = input.parentElement;
          while (parent && parent !== formClone) {
            (parent as HTMLElement).style.fontSize = '10px';
            (parent as HTMLElement).style.overflow = 'visible';
            (parent as HTMLElement).style.whiteSpace = 'normal';
            (parent as HTMLElement).style.wordBreak = 'break-word';
            parent = parent.parentElement;
          }
        });
        
        // 5. Handle buttons - hide non-preview buttons
        const buttons = formClone.querySelectorAll('button');
        buttons.forEach(button => {
          if (!button.textContent?.includes('Preview')) {
            button.style.display = 'none';
          } else {
            // Ensure preview buttons use smaller text
            button.style.fontSize = '10px';
            button.style.whiteSpace = 'normal';
          }
        });
        
        // 6. Apply global styles for better PDF rendering
        const style = document.createElement('style');
        style.textContent = `
          /* Global styles for proper content display */
          .pdf-form-clone * {
            overflow: visible !important;
            font-family: Arial, sans-serif !important;
          }
          
          /* Table specific styling */
          .pdf-form-clone table {
            width: 100% !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
          }
          
          .pdf-form-clone td, 
          .pdf-form-clone th {
            font-size: 13px !important;
            line-height: 1.4 !important;
            word-break: break-word !important;
            overflow: visible !important;
            color: #000000 !important;
            padding: 15px !important;
            height: 60px !important;
            min-height: 50px !important;
            vertical-align: middle !important;
          }
          
          /* Specific rule for table rows to increase height */
          .pdf-form-clone tr {
            height: 60px !important;
            min-height: 50px !important;
          }
          
          /* Fix textarea content display */
          .pdf-form-clone textarea,
          .pdf-form-clone textarea + div {
            white-space: pre-wrap !important;
            word-break: break-word !important;
            font-size: 10px !important;
            line-height: 1.2 !important;
          }
        `;
        document.head.appendChild(style);
        
        // Add class for targeting with CSS
        formClone.classList.add('pdf-form-clone');
        
        // 7. Generate PDF with optimal quality settings
        const canvas = await html2canvas(formClone, {
          scale: 3, // Higher scale for better quality
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#FFFFFF',
          logging: false,
          width: formClone.scrollWidth,
          height: formClone.scrollHeight
        });
        
        // 8. Clean up DOM modifications
        document.body.removeChild(cloneContainer);
        document.head.removeChild(style);
        
        // Get canvas dimensions for PDF creation
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        // Create a PDF with dimensions matching the canvas to ensure a 1:1 replica
        const pdf = new jsPDF({
          orientation: canvasWidth > canvasHeight ? 'l' : 'p',
          unit: 'pt',
          format: [canvasWidth, canvasHeight],
        });

        // Add the entire canvas as a single image with quality optimization
        const imgData = canvas.toDataURL('image/jpeg', 0.5); // Use aggressive JPEG compression
        pdf.addImage(imgData, 'JPEG', 0, 0, canvasWidth, canvasHeight);
 
        // Simulate compression process for visual feedback
        for (let i = 0; i <= 100; i += 10) {
          setCompressionProgress(i);
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        pdf.save(`application-form-${applicationId || 'draft'}.pdf`);

      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setIsPdfGenerating(false);
      setCompressionProgress(0);
    }
  };

  // Function to save data to database
  const saveToDatabaseCallback = useCallback(async (data: typeof formData, isDraft: boolean, anId?: string) => {
    const idToUse = anId || applicationId;

    if (!idToUse) {
      console.error('Application ID is missing, cannot save.');
      setSaveMessage('Error: Application ID missing.');
      setIsSaving(false);
      return;
    }

    if (!isOnline) {
      console.warn('Offline: Save operation skipped for application ID:', idToUse);
      setSaveMessage('Offline: Changes will be saved when back online.');
      return;
    }

    console.log(`[SaveToDB] Attempting to save. ID: ${idToUse}, isDraft: ${isDraft}`);
    setIsSaving(true);
    const currentSaveMessage = isDraft ? 'Saving Draft...' : 'Submitting Application...';
    setSaveMessage(currentSaveMessage);
    setShowSaveIndicator(true);
    
    try {
      const dataToSave = {
        ...data,
      };
      
      const response = await saveFormData(idToUse, dataToSave, isDraft);
      
      if (response.success) {
        const successMsg = isDraft ? 'Draft saved successfully' : 'Application submitted successfully';
        console.log(`[SaveToDB] Success for ID: ${idToUse}. Message: ${successMsg}`);
        setSaveMessage(successMsg);
        if (isDraft && isNewlyGeneratedIdRef.current) {
          isNewlyGeneratedIdRef.current = false;
          console.log('[SaveToDB] Reset isNewlyGeneratedIdRef for ID:', idToUse);
        }
        setTimeout(() => setShowSaveIndicator(false), 2000);
      } else {
        setSaveMessage('Error saving');
        setTimeout(() => setShowSaveIndicator(false), 3000);
      }
    } catch (error) {
      console.error('Error saving form data:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setShowSaveIndicator(false), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, isOnline]);

  // Effect for initial auto-save of a newly generated ID
  useEffect(() => {
    if (applicationId && isNewlyGeneratedIdRef.current && isOnline && Object.keys(formData).some(key => formData[key as keyof FormData] !== '' && key !== 'errors' && key !== 'applicationNoOfficeUse')) {
      console.log(`[ApplicationFormPage5 Effect AutoSave] Application ID ${applicationId} is newly generated. Attempting to save as draft.`);
      saveToDatabaseCallback(formData, true, applicationId);
    }
  }, [applicationId, formData, saveToDatabaseCallback, isOnline]);

  // Helper function to get file name from path or File object
  const getFileName = (file: File | string | null, defaultName: string = 'Unknown file'): string => {
    if (!file) return defaultName;
    
    if (typeof file === 'string') {
      // Extract file name from path
      const pathParts = file.split('/');
      return pathParts[pathParts.length - 1] || defaultName;
    } else {
      // It's a File object
      return file.name || defaultName;
    }
  };

  // Handle file upload
  const handleFileUpload = async (file: File, fieldName: UploadableField) => {
    console.log(`[handleFileUpload] Starting upload for field: ${fieldName}, file:`, file);
    
    if (!applicationId) {
      console.error('Cannot upload file: Missing application ID');
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'error' }));
      setUploadMessage(prev => ({ ...prev, [fieldName]: 'Error: Missing application ID' }));
      return;
    }

    // Global file type validation - block unsupported file types
    const blockedExtensions = ['.doc', '.docx', '.xlsx', '.xls', '.txt', '.bmp'];
    const blockedMimeTypes = [
      'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'image/bmp'
    ];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (blockedExtensions.includes(fileExtension) || blockedMimeTypes.includes(file.type)) {
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'error' }));
      setUploadMessage(prev => ({ ...prev, [fieldName]: `File type "${fileExtension}" is not supported. Please upload only JPG, JPEG, PNG, or PDF files.` }));
      return;
    }

    try {
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'uploading' }));
      setUploadMessage(prev => ({ ...prev, [fieldName]: 'Uploading...' }));
      setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));

      const uploadResponse = await uploadFormFile(applicationId, file, fieldName, (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(prev => ({ ...prev, [fieldName]: percentComplete }));
        }
      });

      console.log(`[Upload Success] Field: ${fieldName}, API Response:`, uploadResponse);
      const filePath = uploadResponse.filePath;
      
      const updatedFormData = { ...formData, [fieldName]: filePath };
      setFormData(updatedFormData);
      
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'success' }));
      setUploadMessage(prev => ({ ...prev, [fieldName]: 'File uploaded successfully' }));
      
      // Clear success message after a short timeout, but keep status as 'success'
      setTimeout(() => {
        setUploadMessage(prev => ({ ...prev, [fieldName]: '' }));
      }, 500);
      
    } catch (error) {
      console.error(`[Upload Error] Field: ${fieldName}, Error:`, error);
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'error' }));
      setUploadMessage(prev => ({ ...prev, [fieldName]: 'Upload failed. Please try again.' }));
      
      setTimeout(() => {
        setUploadMessage(prev => ({ ...prev, [fieldName]: '' }));
      }, 3000);
    }
  };

  // Handle file change
  const handleFileChange = (fieldName: UploadableField, file: File | null) => {
    console.log(`[handleFileChange] Field: ${fieldName}, File:`, file);
    
    if (!file) {
      setFormData(prev => ({ ...prev, [fieldName]: null }));
      return;
    }
    
    // Only proceed with upload if we have an actual File object
    handleFileUpload(file, fieldName);
  };

  const handleFillDummyData = () => {
    console.log('[ApplicationFormPage5 FillDummyData] Filling form with dummy data.');
    const dummyFiles: { [key: string]: string } = {
      ownershipDocument: 'dummy_ownershipDocument.pdf',
      searchTitleReport: 'dummy_searchTitleReport.pdf',
      notarizedAffidavit: 'dummy_notarizedAffidavit.pdf',
      propertyCard: 'dummy_propertyCard.pdf',
      ferfarMutation: 'dummy_ferfarMutation.pdf',
      demarcationPlan: 'dummy_demarcationPlan.pdf',
      plotDetails: 'dummy_plotDetails.pdf',
      roadDPPlan: 'dummy_roadDPPlan.pdf',
      dpOpinion: 'dummy_dpOpinion.pdf',
      zoneCertificate: 'dummy_zoneCertificate.pdf',
      propertyTaxReceipts: 'dummy_propertyTaxReceipts.pdf',
      partnershipDeed: 'dummy_partnershipDeed.pdf',
      registrationCertificate: 'dummy_registrationCertificate.pdf',
      landLeaseAgreement: 'dummy_landLeaseAgreement.pdf',
      faLnMap: 'dummy_faLnMap.pdf',
      landUsageClearance: 'dummy_landUsageClearance.pdf',
      surveyPlan: 'dummy_surveyPlan.pdf',
      nocLandlord: 'dummy_nocLandlord.pdf',
      nocAuthority: 'dummy_nocAuthority.pdf',
      advocateOpinion: 'dummy_advocateOpinion.pdf',
      consentLetters: 'dummy_consentLetters.pdf',
    };
    const updatedDummyData: FormData = {
      ...formData, // Start with current formData to preserve applicationId etc.
      applicationNoOfficeUse: formData.applicationNoOfficeUse || applicationId || '',
      accountDetails: [
        { id: '1', type: 'Savings Account', details: 'SBI Main Branch, Account No: 12345678901', holderName: 'John Doe', relationship: 'Self', amount: '500000' },
        { id: '2', type: 'Fixed Deposit', details: 'HDFC Bank, FD No: FD987654321', holderName: 'Jane Doe', relationship: 'Spouse', amount: '1000000' }
      ],
      anyOtherDetails: 'Property has been in family ownership for 25 years. No legal disputes or pending litigation. Property is clear of all encumbrances.',
      ...dummyFiles,
      errors: {},
    };
    setFormData(updatedDummyData);
    setSaveMessage('Dummy data filled.');
    setShowSaveIndicator(false);
  };

  const debouncedSave = useCallback((newData: FormData) => {
    console.log('[DebouncedSave] Invoked. Clearing previous timeout if any.');
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      console.log('[DebouncedSave] Timeout executed. Checking conditions to save.');
      if (applicationId) {
        console.log(`[DebouncedSave] Conditions met. ApplicationId: ${applicationId}. Proceeding to save.`);
        console.log(`[DebouncedSave] Triggering auto-save (as draft) for ID: ${applicationId}`);
        saveToDatabaseCallback({ ...newData, applicationNoOfficeUse: applicationId }, true, applicationId);
      } else {
        console.log(`[DebouncedSave] Conditions NOT met. Skipping save. ApplicationId: ${applicationId}`);
      }
    }, 1000);
  }, [saveToDatabaseCallback, applicationId]);

  // Removed interval-based auto-save - using navigation-based save modal instead

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    // Removed auto-save on every keystroke - use SAVE DRAFT button or navigation modal instead
  };

  // Note: Using the handleFileChange function defined above for all file upload operations

  const handleAccountDetailChange = (id: string, field: keyof AccountRow, value: string) => {
    const updatedAccountDetails = formData.accountDetails.map(row => 
      row.id === id ? { ...row, [field]: value } : row
    );
    const updatedFormData = { ...formData, accountDetails: updatedAccountDetails };
    setFormData(updatedFormData);
    // Removed auto-save on every keystroke - use SAVE DRAFT button or navigation modal instead
  };

  const addAccountRow = () => {
    const newId = (formData.accountDetails.length + 1).toString();
    const newRow: AccountRow = { id: newId, type: '', details: '', holderName: '', relationship: '', amount: '' };
    const updatedFormData = { ...formData, accountDetails: [...formData.accountDetails, newRow] };
    setFormData(updatedFormData);
  };

  const removeAccountRow = (id: string) => {
    if (formData.accountDetails.length > 1) {
      const updatedAccountDetails = formData.accountDetails.filter(row => row.id !== id);
      const updatedFormData = { ...formData, accountDetails: updatedAccountDetails };
      setFormData(updatedFormData);
    }
  };

  const handleZoomIn = () => {
    setZoomScale(prev => Math.min(prev + 0.1, 1.5));
  };
  
  const handleZoomOut = () => {
    setZoomScale(prev => Math.max(prev - 0.1, 0.6));
  };

  const handleNextButtonClick = () => {
    // Show save modal before navigation
    if (applicationId && isOnline) {
      setPendingNavigation('/form/page6');
      setShowSaveModal(true);
    } else {
      navigate('/form/page6');
    }
  };

  // Handle save modal actions
  const handleSaveAndNavigate = async () => {
    setShowSaveModal(false);
    
    if (applicationId && isOnline) {
      console.log('[Navigation Page5] Saving draft before navigating');
      await saveToDatabaseCallback(formData, true, applicationId);
      
      // Wait 1.5 seconds so user can see the "Draft saved successfully" message
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
    
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleSkipSaveAndNavigate = () => {
    setShowSaveModal(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  // Handle navigation from vertical nav bar - show save modal
  const handleNavBarNavigation = (targetPath: string) => {
    if (applicationId && isOnline) {
      setPendingNavigation(targetPath);
      setShowSaveModal(true);
    } else {
      navigate(targetPath);
    }
  };

  // Calculate form progress whenever form data changes
  useEffect(() => {
    const totalFields = 23; // 1 anyOtherDetails + 1 accountDetails (group) + 21 file uploads
    let filledFields = 0;
    
    // Check any other details
    if (formData.anyOtherDetails && formData.anyOtherDetails.trim() !== '') filledFields++;
    
    // Check account details (at least one row with some data)
    const hasAccountData = formData.accountDetails.some(row => 
      row.type.trim() !== '' || row.details.trim() !== '' || row.holderName.trim() !== '' || 
      row.relationship.trim() !== '' || row.amount.trim() !== ''
    );
    if (hasAccountData) filledFields++;
    
    // Check file uploads
    const fileFields = [
      'ownershipDocument', 'searchTitleReport', 'notarizedAffidavit', 'propertyCard', 'ferfarMutation',
      'demarcationPlan', 'plotDetails', 'roadDPPlan', 'dpOpinion', 'zoneCertificate',
      'propertyTaxReceipts', 'partnershipDeed', 'registrationCertificate', 'landLeaseAgreement',
      'faLnMap', 'landUsageClearance', 'surveyPlan', 'nocLandlord', 'nocAuthority',
      'advocateOpinion', 'consentLetters'
    ];
    
    fileFields.forEach(field => {
      if (formData[field as keyof FormData]) filledFields++;
    });

    const progressPercentage = totalFields > 0 ? (filledFields / totalFields) * 100 : 0;
    setFormProgress(progressPercentage);
  }, [formData]);

  // Effect for online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <>
      {/* Progress bar */}
      <ProgressBarContainer>
        <ProgressBarFill progress={formProgress} />
      </ProgressBarContainer>
      <ProgressLabel>Page 5/14 • {Math.round(formProgress)}% complete</ProgressLabel>
      
      {/* Zoom controls */}
      <ZoomControlsContainer>
        <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
        <ZoomButton onClick={handleZoomOut}>−</ZoomButton>
      </ZoomControlsContainer>
      
      {/* Save indicator */}
      <SaveIndicator visible={showSaveIndicator}>
        {saveMessage}
      </SaveIndicator>

      {!isOnline && (
        <OfflineIndicator>
          You are currently offline. Changes will not be saved until you reconnect.
        </OfflineIndicator>
      )}
      
      <FloatingBackButton onClick={() => navigate('/form/page4')} aria-label="Back to previous page">
        ←
      </FloatingBackButton>
      <div style={{ position: 'fixed', top: '80px', left: '30px', zIndex: 1001 }}>
        <button 
          onClick={() => setShowBrochureModal(true)}
          style={{ 
            backgroundColor: '#2196f3', 
            color: '#ffffff', 
            borderRadius: '4px', 
            padding: '8px 15px',
            border: '1px solid #1976d2',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          📄 View Brochure
        </button>
      </div>
      
      {showDummyDataButton && (
        <div style={{ position: 'fixed', top: '80px', right: '30px', zIndex: 1001, display: 'block' }}>
          <button 
            onClick={handleFillDummyData} 
            style={{ 
              backgroundColor: '#ffc107', 
              color: '#212529', 
              borderRadius: '4px', 
              padding: '8px 15px',
              border: '1px solid #e0a800',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            Fill Dummy Data
          </button>
        </div>
      )}
      
      <FormPageNavigator currentPage={5} totalPages={14} targetFormRef={formRef} zoomScale={zoomScale} />
      
      <div style={{ position: 'fixed', right: '-25px', top: '605px', zIndex: 999 }}>
        <NextPageButton 
          onClick={handleNextButtonClick}
          style={{ 
            height: '35px', 
            width: '140px', 
            borderRadius: '4px', 
            whiteSpace: 'nowrap',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '0 10px'
          }}
        >
          Next Page 6/14 →
        </NextPageButton>
      </div>
      
      <PageWrapper scale={zoomScale}>
        <A4PageContainer ref={formRef}>
          <FormContentContainer>
            {/* Account Details Table Section */}
            <FormSection>
              <FormLabel>Account Details:</FormLabel>
              <AddRowButton onClick={addAccountRow}>+ Add Row</AddRowButton>
              
              <Table>
                <thead>
                  <tr>
                    <TableHeader style={{ width: '15%' }}>Type</TableHeader>
                    <TableHeader style={{ width: '30%' }}>Details of A/c, etc.</TableHeader>
                    <TableHeader style={{ width: '20%' }}>Name of the Holder</TableHeader>
                    <TableHeader style={{ width: '15%' }}>Relationship</TableHeader>
                    <TableHeader style={{ width: '15%' }}>Amount</TableHeader>
                    <TableHeader style={{ width: '5%' }}>Action</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {formData.accountDetails.map((row) => (
                    <tr key={row.id}>
                      <TableCell>
                        <TableInput
                          value={row.type}
                          onChange={(e) => handleAccountDetailChange(row.id, 'type', e.target.value)}
                          placeholder="Account type"
                          disabled={isViewMode}
                        />
                      </TableCell>
                      <TableCell>
                        <TableInput
                          value={row.details}
                          onChange={(e) => handleAccountDetailChange(row.id, 'details', e.target.value)}
                          placeholder="Account details"
                          disabled={isViewMode}
                        />
                      </TableCell>
                      <TableCell>
                        <TableInput
                          value={row.holderName}
                          onChange={(e) => handleAccountDetailChange(row.id, 'holderName', e.target.value)}
                          placeholder="Holder name"
                          disabled={isViewMode}
                        />
                      </TableCell>
                      <TableCell>
                        <TableInput
                          value={row.relationship}
                          onChange={(e) => handleAccountDetailChange(row.id, 'relationship', e.target.value)}
                          placeholder="Relationship"
                          disabled={isViewMode}
                        />
                      </TableCell>
                      <TableCell>
                        <TableInput
                          value={row.amount}
                          onChange={(e) => handleAccountDetailChange(row.id, 'amount', e.target.value)}
                          placeholder="Amount"
                          disabled={isViewMode}
                        />
                      </TableCell>
                      <TableCell>
                        {!isViewMode && formData.accountDetails.length > 1 && (
                          <RemoveRowButton onClick={() => removeAccountRow(row.id)}>
                            ×
                          </RemoveRowButton>
                        )}
                      </TableCell>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </FormSection>

            {/* Any Other Details Section */}
            <FormSection>
              <FormLabel htmlFor="anyOtherDetails">5. Any Other Details:</FormLabel>
              <FormTextarea
                id="anyOtherDetails"
                name="anyOtherDetails"
                value={formData.anyOtherDetails}
                onChange={handleChange}
                placeholder="Enter any other relevant details..."
                disabled={isViewMode}
              />
            </FormSection>

            {/* Document Upload Sections */}
            <FormSection>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #6cb288', paddingBottom: '10px' }}>
                Required Documents
              </h3>

              <FileUploadSection>
                <FileUploadLabel>1. Certified Copy of Ownership / Title Document:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('ownershipDocument', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.ownershipDocument === 'uploading'}
                    />
                    {formData.ownershipDocument && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.ownershipDocument, 'ownership-document')}
                      >
                        Preview {getFileName(formData.ownershipDocument, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.ownershipDocument === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.ownershipDocument || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.ownershipDocument && uploadProgress.ownershipDocument > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.ownershipDocument || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.ownershipDocument && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.ownershipDocument === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.ownershipDocument === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.ownershipDocument}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>2. Search & Title Report by Advocate of the Applicant:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('searchTitleReport', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.searchTitleReport === 'uploading'}
                    />
                    {formData.searchTitleReport && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.searchTitleReport, 'search-title-report')}
                      >
                        Preview {getFileName(formData.searchTitleReport, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.searchTitleReport === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.searchTitleReport || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.searchTitleReport && uploadProgress.searchTitleReport > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.searchTitleReport || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.searchTitleReport && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.searchTitleReport === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.searchTitleReport === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.searchTitleReport}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>3. Notarized Affidavit of Title of Ownership (Appendix D) on Rs. 100 stamp paper:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('notarizedAffidavit', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.notarizedAffidavit === 'uploading'}
                    />
                    {formData.notarizedAffidavit && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.notarizedAffidavit, 'notarized-affidavit')}
                      >
                        Preview {getFileName(formData.notarizedAffidavit, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.notarizedAffidavit === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.notarizedAffidavit || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.notarizedAffidavit && uploadProgress.notarizedAffidavit > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.notarizedAffidavit || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.notarizedAffidavit && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.notarizedAffidavit === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.notarizedAffidavit === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.notarizedAffidavit}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>4. Property card or 7/12 extract of a recent date (original):</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('propertyCard', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.propertyCard === 'uploading'}
                    />
                    {formData.propertyCard && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.propertyCard, 'property-card')}
                      >
                        Preview {getFileName(formData.propertyCard, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.propertyCard === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.propertyCard || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.propertyCard && uploadProgress.propertyCard > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.propertyCard || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.propertyCard && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.propertyCard === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.propertyCard === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.propertyCard}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>5. Ferfar/Mutation Record (Form of application):</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('ferfarMutation', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.ferfarMutation === 'uploading'}
                    />
                    {formData.ferfarMutation && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.ferfarMutation, 'ferfar-mutation')}
                      >
                        Preview {getFileName(formData.ferfarMutation, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.ferfarMutation === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.ferfarMutation || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.ferfarMutation && uploadProgress.ferfarMutation > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.ferfarMutation || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.ferfarMutation && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.ferfarMutation === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.ferfarMutation === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.ferfarMutation}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>6. Latest Demarcation Plan (Original):</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('demarcationPlan', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.demarcationPlan === 'uploading'}
                    />
                    {formData.demarcationPlan && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.demarcationPlan, 'demarcation-plan')}
                      >
                        Preview {getFileName(formData.demarcationPlan, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.demarcationPlan === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.demarcationPlan || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.demarcationPlan && uploadProgress.demarcationPlan > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.demarcationPlan || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.demarcationPlan && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.demarcationPlan === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.demarcationPlan === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.demarcationPlan}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>7. Details of Offered plot in the total plot on Certified copy of Demarcation Plan:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('plotDetails', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.plotDetails === 'uploading'}
                    />
                    {formData.plotDetails && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.plotDetails, 'plot-details')}
                      >
                        Preview {getFileName(formData.plotDetails, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.plotDetails === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.plotDetails || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.plotDetails && uploadProgress.plotDetails > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.plotDetails || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.plotDetails && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.plotDetails === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.plotDetails === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.plotDetails}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>8. Certified copy of latest Sanctioned road DP plan from local Authority:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('roadDPPlan', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.roadDPPlan === 'uploading'}
                    />
                    {formData.roadDPPlan && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.roadDPPlan, 'road-dp-plan')}
                      >
                        Preview {getFileName(formData.roadDPPlan, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.roadDPPlan === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.roadDPPlan || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.roadDPPlan && uploadProgress.roadDPPlan > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.roadDPPlan || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.roadDPPlan && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.roadDPPlan === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.roadDPPlan === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.roadDPPlan}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>9. Latest DP opinion of the total plot from Local Municipal Corporation:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('dpOpinion', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.dpOpinion === 'uploading'}
                    />
                    {formData.dpOpinion && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.dpOpinion, 'dp-opinion')}
                      >
                        Preview {getFileName(formData.dpOpinion, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.dpOpinion === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.dpOpinion || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.dpOpinion && uploadProgress.dpOpinion > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.dpOpinion || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.dpOpinion && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.dpOpinion === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.dpOpinion === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.dpOpinion}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>10. Zone Certificate (Copy):</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('zoneCertificate', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.zoneCertificate === 'uploading'}
                    />
                    {formData.zoneCertificate && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.zoneCertificate, 'zone-certificate')}
                      >
                        Preview {getFileName(formData.zoneCertificate, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.zoneCertificate === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.zoneCertificate || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.zoneCertificate && uploadProgress.zoneCertificate > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.zoneCertificate || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.zoneCertificate && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.zoneCertificate === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.zoneCertificate === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.zoneCertificate}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>11. Previous 5 years property tax/dues payment receipts along with latest tax NOC:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('propertyTaxReceipts', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.propertyTaxReceipts === 'uploading'}
                    />
                    {formData.propertyTaxReceipts && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.propertyTaxReceipts, 'property-tax-receipts')}
                      >
                        Preview {getFileName(formData.propertyTaxReceipts, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.propertyTaxReceipts === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.propertyTaxReceipts || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.propertyTaxReceipts && uploadProgress.propertyTaxReceipts > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.propertyTaxReceipts || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.propertyTaxReceipts && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.propertyTaxReceipts === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.propertyTaxReceipts === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.propertyTaxReceipts}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>12. Partnership Firm: Partnership Deed along with certificate of registration:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('partnershipDeed', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.partnershipDeed === 'uploading'}
                    />
                    {formData.partnershipDeed && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.partnershipDeed, 'partnership-deed')}
                      >
                        Preview {getFileName(formData.partnershipDeed, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.partnershipDeed === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.partnershipDeed || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.partnershipDeed && uploadProgress.partnershipDeed > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.partnershipDeed || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.partnershipDeed && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.partnershipDeed === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.partnershipDeed === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.partnershipDeed}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>13. Certificate of Registration for Co-operatives/Corporate Bodies:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('registrationCertificate', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.registrationCertificate === 'uploading'}
                    />
                    {formData.registrationCertificate && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.registrationCertificate, 'registration-certificate')}
                      >
                        Preview {getFileName(formData.registrationCertificate, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.registrationCertificate === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.registrationCertificate || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.registrationCertificate && uploadProgress.registrationCertificate > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.registrationCertificate || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.registrationCertificate && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.registrationCertificate === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.registrationCertificate === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.registrationCertificate}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>14. Land Lease agreement showing Commercial Status of the offered site:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('landLeaseAgreement', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.landLeaseAgreement === 'uploading'}
                    />
                    {formData.landLeaseAgreement && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.landLeaseAgreement, 'land-lease-agreement')}
                      >
                        Preview {getFileName(formData.landLeaseAgreement, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.landLeaseAgreement === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.landLeaseAgreement || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.landLeaseAgreement && uploadProgress.landLeaseAgreement > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.landLeaseAgreement || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.landLeaseAgreement && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.landLeaseAgreement === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.landLeaseAgreement === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.landLeaseAgreement}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>15. FA/LN MAP/Subdivision Map (if applicable):</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('faLnMap', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.faLnMap === 'uploading'}
                    />
                    {formData.faLnMap && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.faLnMap, 'fa-ln-map')}
                      >
                        Preview {getFileName(formData.faLnMap, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.faLnMap === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.faLnMap || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.faLnMap && uploadProgress.faLnMap > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.faLnMap || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.faLnMap && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.faLnMap === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.faLnMap === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.faLnMap}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>16. Land usage clearance between MIDC & applicant:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('landUsageClearance', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.landUsageClearance === 'uploading'}
                    />
                    {formData.landUsageClearance && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.landUsageClearance, 'land-usage-clearance')}
                      >
                        Preview {getFileName(formData.landUsageClearance, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.landUsageClearance === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.landUsageClearance || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.landUsageClearance && uploadProgress.landUsageClearance > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.landUsageClearance || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.landUsageClearance && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.landUsageClearance === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.landUsageClearance === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.landUsageClearance}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>17. Survey plan issued by MIDC and Plot Handover letter:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('surveyPlan', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.surveyPlan === 'uploading'}
                    />
                    {formData.surveyPlan && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.surveyPlan, 'survey-plan')}
                      >
                        Preview {getFileName(formData.surveyPlan, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.surveyPlan === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.surveyPlan || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.surveyPlan && uploadProgress.surveyPlan > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.surveyPlan || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.surveyPlan && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.surveyPlan === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.surveyPlan === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.surveyPlan}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>18. NOC from Landlord (in case of lease hold land/Copy):</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('nocLandlord', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.nocLandlord === 'uploading'}
                    />
                    {formData.nocLandlord && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.nocLandlord, 'noc-landlord')}
                      >
                        Preview {getFileName(formData.nocLandlord, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.nocLandlord === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.nocLandlord || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.nocLandlord && uploadProgress.nocLandlord > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.nocLandlord || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.nocLandlord && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.nocLandlord === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.nocLandlord === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.nocLandlord}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>19. NOC from MHADA, MIDC, Municipal Corporation or applicable local authority:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('nocAuthority', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.nocAuthority === 'uploading'}
                    />
                    {formData.nocAuthority && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.nocAuthority, 'noc-authority')}
                      >
                        Preview {getFileName(formData.nocAuthority, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.nocAuthority === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.nocAuthority || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.nocAuthority && uploadProgress.nocAuthority > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.nocAuthority || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.nocAuthority && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.nocAuthority === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.nocAuthority === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.nocAuthority}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>20. Advocate Opinion:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('advocateOpinion', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.advocateOpinion === 'uploading'}
                    />
                    {formData.advocateOpinion && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.advocateOpinion, 'advocate-opinion')}
                      >
                        Preview {getFileName(formData.advocateOpinion, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.advocateOpinion === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.advocateOpinion || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.advocateOpinion && uploadProgress.advocateOpinion > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.advocateOpinion || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.advocateOpinion && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.advocateOpinion === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.advocateOpinion === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.advocateOpinion}
                    </div>
                  )}
                </div>
              </FileUploadSection>

              <FileUploadSection>
                <FileUploadLabel>21. Consent letters from concerned line in white format on Rs. 100 stamp paper duly notarized:</FileUploadLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileInput
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('consentLetters', e.target.files?.[0] || null)}
                      disabled={isViewMode || uploadStatus.consentLetters === 'uploading'}
                    />
                    {formData.consentLetters && (
                      <PreviewButton
                        onClick={() => handleOpenFilePreview(formData.consentLetters, 'consent-letters')}
                      >
                        Preview {getFileName(formData.consentLetters, 'file')}
                      </PreviewButton>
                    )}
                  </div>
                  
                  {/* Upload status and progress bar */}
                  {uploadStatus.consentLetters === 'uploading' && (
                    <>
                      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                        <div 
                          style={{ 
                            position: 'absolute', 
                            height: '100%', 
                            width: `${uploadProgress.consentLetters || 0}%`, 
                            backgroundColor: '#6cb288',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: uploadProgress.consentLetters && uploadProgress.consentLetters > 50 ? '#fff' : '#000'
                          }}
                        >
                          {uploadProgress.consentLetters || 0}%
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Success or error message */}
                  {uploadMessage.consentLetters && (
                    <div 
                      style={{ 
                        padding: '4px', 
                        backgroundColor: uploadStatus.consentLetters === 'error' ? '#f8d7da' : '#d4edda',
                        color: uploadStatus.consentLetters === 'error' ? '#721c24' : '#155724',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '4px'
                      }}
                    >
                      {uploadMessage.consentLetters}
                    </div>
                  )}
                </div>
              </FileUploadSection>
            </FormSection>

            {/* Any Other Details Section */}
            <FormSection>
              <FormLabel htmlFor="anyOtherDetails">Any Other Details:</FormLabel>
              <StyledTextArea
                id="anyOtherDetails"
                name="anyOtherDetails"
                value={formData.anyOtherDetails}
                onChange={handleChange}
                rows={4}
                placeholder="Provide any other relevant details here..."
                disabled={isViewMode}
              />
            </FormSection>
          </FormContentContainer>
          <PageNumber>5</PageNumber>
        </A4PageContainer>
        
        <FilePreviewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          fileUrl={modalFileUrl}
          fileName={modalFileName}
          fileType={modalFileType}
        />
      </PageWrapper>
      
      {isPdfGenerating && (
        <ModalOverlay>
          <ModalContent>
            <h3>Generating PDF</h3>
            <p>Please wait... Generating PDF</p>
            <ProgressBarContainerModal>
              <ProgressBarFillModal progress={compressionProgress} />
            </ProgressBarContainerModal>
            <ProgressText>Compression: {compressionProgress}%</ProgressText>
          </ModalContent>
        </ModalOverlay>
      )}
      
      {/* Empty ActionButtonsContainer to maintain structure */}
      <ActionButtonsContainer style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '800px', zIndex: 1000 }}>
        {/* Next Page button moved near FormPageNavigator */}
      </ActionButtonsContainer>
      
      {/* Session Timer */}
      {!isViewMode && (
        <SessionTimer 
          sessionDuration={30}
          warningTime={5}
          onSessionExpired={() => navigate('/')}
          onSaveState={async () => {
            if (applicationId) {
              await saveToDatabaseCallback(formData, true, applicationId);
            }
          }}
        />
      )}

      {/* Home Icon Button - Above Save Icon */}
      <button
        onClick={() => navigate('/user-dashboard')}
        title="Go to Dashboard"
        style={{
          position: 'fixed',
          left: '30px',
          bottom: '160px',
          zIndex: 1000,
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          border: 'none',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '22px',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
          transition: 'all 0.2s ease'
        }}
      >
        🏠
      </button>

      {/* Green Floppy Save Icon Button - Above Back Button */}
      {!isViewMode && (
        <button
          onClick={() => saveToDatabaseCallback(formData, true, applicationId)}
          disabled={isSaving || !applicationId || !isOnline}
          title="Save Draft"
          style={{
            position: 'fixed',
            left: '30px',
            bottom: '100px',
            zIndex: 1000,
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: 'none',
            background: isSaving ? '#a5d6a7' : 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
            color: 'white',
            cursor: isSaving || !applicationId || !isOnline ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px',
            boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
            transition: 'all 0.2s ease',
            opacity: isSaving || !applicationId || !isOnline ? 0.7 : 1
          }}
        >
          💾
        </button>
      )}
      
      {isViewMode && (
        <PayNowButton 
          onClick={handleDownloadPdf} 
          disabled={!isOnline}
          style={{ position: 'fixed', left: '95px', bottom: '31.5px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 'bold', letterSpacing: '0.2px' }}
        > 
          DOWNLOAD PDF
        </PayNowButton>
      )}

      {/* Save Progress Modal */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2200,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '28px 32px',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            maxWidth: '380px',
            width: '90%',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2.5em', marginBottom: '12px' }}>💾</div>
            <h3 style={{ margin: '0 0 10px 0', color: '#2c3e50', fontSize: '1.15em', fontWeight: 600 }}>
              Save your progress?
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.9em', color: '#666', lineHeight: 1.5 }}>
              Would you like to save your form before continuing?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={handleSkipSaveAndNavigate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f1f1f1',
                  color: '#555',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9em',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleSaveAndNavigate}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9em',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const ProgressBarContainerModal = styled.div`
  width: 100%;
  height: 20px;
  background-color: #e9ecef;
  border-radius: 10px;
  margin-top: 20px;
  overflow: hidden;
`;

const ProgressBarFillModal = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%; // Dynamic width based on progress
  background-color: #6cb288;
  transition: width 0.3s ease-in-out;
`;

const ProgressText = styled.p`
  margin-top: 10px;
  font-size: 0.9em;
  color: #555;
`;

export default ApplicationFormPage5;